import React, { useState, useMemo } from 'react';
import styles from './DescriptionRentalTermsTabs.module.css';
import classNames from 'classnames';
import { FAQ } from '../../../components';
import { isHTMLString } from '../../../util/data';
import { useConfiguration } from '../../../context/configurationContext';

export default function DescriptionRentalTermsTabs({
  description,
  condition,
  rentalTerms,
  faqs,
  intl,
  listingCurrency,
  currentUserCurrency,
  exchangeRates = []
}) {
  const config = useConfiguration();
  const [activeTab, setActiveTab] = useState('description');
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Condition/cancellation-policy pills shown above the description text.
  // Same tag+suffix formatting previously used for the (now-removed) header
  // badge row — reused here, not reinvented, so translations stay in sync.
  const formatTagWithSuffix = (rawTag, suffixMessageId) => {
    if (!rawTag || !rawTag.length) return null;
    const translatedTag = intl.formatMessage({ id: `ListingPage.tag.${rawTag}` });
    const suffix = intl.formatMessage({ id: suffixMessageId });
    return translatedTag.includes(suffix) ? translatedTag : `${translatedTag} ${suffix}`;
  };
  const conditionBadge = condition === 'its_a_service'
    ? null
    : formatTagWithSuffix(condition, 'ListingPage.formatTag.condition');
  const cancelationBadge = formatTagWithSuffix(
    rentalTerms?.cancelationPolicy,
    'ListingPage.formatTag.cancelation'
  );

  // Helper function to count paragraphs in HTML string
  const countParagraphs = (htmlString) => {
    if (typeof window === 'undefined') return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.querySelectorAll('p').length;
  };

  // Determine if mobile view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Helper function to truncate HTML content
  const truncateHTML = (htmlString, maxLength = 1000) => {
    if (typeof window === 'undefined') return htmlString;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    let currentLength = 0;
    let truncatedContent = '';
    let wasTruncated = false;

    const walkDOM = (node) => {
      if (!node || !node.nodeType) return;
      if (currentLength >= maxLength) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        const remainingLength = maxLength - currentLength;

        if (currentLength + text.length <= maxLength) {
          truncatedContent += text;
          currentLength += text.length;
        } else {
          truncatedContent += text.substring(0, remainingLength);
          currentLength = maxLength;
          wasTruncated = true;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        truncatedContent += `<${tagName}`;

        // Add attributes
        for (let i = 0; i < node.attributes.length; i++) {
          const attr = node.attributes[i];
          truncatedContent += ` ${attr.name}="${attr.value}"`;
        }
        truncatedContent += '>';

        // Process child nodes
        for (let i = 0; i < node.childNodes.length; i++) {
          if (currentLength >= maxLength) break;
          walkDOM(node.childNodes[i]);
        }

        if (currentLength < maxLength) {
          truncatedContent += `</${tagName}>`;
        }
      }
    };

    walkDOM(tempDiv);
    return wasTruncated ? truncatedContent + '...' : truncatedContent;
  };

  // Determine if description should be truncated
  const shouldTruncate = useMemo(() => {
    if (!description) return false;

    const maxLength = isMobile ? 200 : 1000;

    if (isHTMLString(description)) {
      const paragraphCount = countParagraphs(description);
      const charCount = description.replace(/<[^>]*>/g, '').length;
      return charCount > maxLength || paragraphCount > 3;
    } else {
      const charCount = description.length;
      const paragraphCount = (description.match(/\n\s*\n/g) || []).length + 1;
      return charCount > maxLength || paragraphCount > 3;
    }
  }, [description, isMobile]);

  // Get truncated description
  const truncatedDescription = useMemo(() => {
    if (!shouldTruncate || showFullDescription) return description;

    const maxLength = isMobile ? 200 : 1000;

    if (isHTMLString(description)) {
      const truncated = truncateHTML(description, maxLength);
      // Check if the content was actually truncated (ends with ...)
      if (truncated.endsWith('...')) {
        return truncated;
      } else {
        // If not truncated by truncateHTML but still longer than maxLength, add ...
        const charCount = description.replace(/<[^>]*>/g, '').length;
        return charCount > maxLength ? truncated + '...' : truncated;
      }
    } else {
      return description.length > maxLength ? description.substring(0, maxLength) + '...' : description;
    }
  }, [description, shouldTruncate, showFullDescription, isMobile]);

  // Get listing fields from config
  const listingFields = config?.listing?.listingFields || [];
  
  // Helper to get full text for security deposit
  const getSecurityDepositText = useMemo(() => {
    if (!rentalTerms?.cashSecurityDeposit) return '';
    
    const securityDepositField = listingFields.find(field => field.key === 'security_deposit');
    if (!securityDepositField?.enumOptions) return '';
    
    const value = String(rentalTerms.cashSecurityDeposit);
    const option = securityDepositField.enumOptions.find(opt => String(opt.option) === value);
    return option?.label || '';
  }, [rentalTerms?.cashSecurityDeposit, listingFields]);
  
  // Helper to get full text for cancellation policy
  const getCancellationPolicyText = useMemo(() => {
    if (!rentalTerms?.cancelationPolicy) return '';
    
    const cancellationPolicyField = listingFields.find(field => field.key === 'cancelation_policy');
    if (!cancellationPolicyField?.enumOptions) return '';
    
    const value = String(rentalTerms.cancelationPolicy);
    const option = cancellationPolicyField.enumOptions.find(opt => String(opt.option) === value);
    
    if (!option) return '';
    
    // Combine label and subLabel if subLabel exists
    return option.subLabel 
      ? `${option.label} – ${option.subLabel}`
      : option.label;
  }, [rentalTerms?.cancelationPolicy, listingFields]);

  const descriptionTabId = 'listing-info-tab-description';
  const rentalTermsTabId = 'listing-info-tab-rental-terms';
  const faqTabId = 'listing-info-tab-faq';
  const descriptionPanelId = 'listing-info-panel-description';
  const rentalTermsPanelId = 'listing-info-panel-rental-terms';
  const faqPanelId = 'listing-info-panel-faq';

  return (
    <section className={styles.panel}>
      <nav className={styles.tabs} aria-label="Rental Information Tabs" role="tablist">
        <button
          className={`${styles.tab} ${activeTab === 'description' ? styles.active : ''}`}
          onClick={() => setActiveTab('description')}
          type="button"
          role="tab"
          id={descriptionTabId}
          aria-controls={descriptionPanelId}
          aria-selected={activeTab === 'description'}
        >
          {intl.formatMessage({ id: 'DescriptionRentalTermsTabs.description' })}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'rentalTerms' ? styles.active : ''}`}
          onClick={() => setActiveTab('rentalTerms')}
          type="button"
          role="tab"
          id={rentalTermsTabId}
          aria-controls={rentalTermsPanelId}
          aria-selected={activeTab === 'rentalTerms'}
        >
          {intl.formatMessage({ id: 'DescriptionRentalTermsTabs.rentalTerms' })}

        </button>
        {faqs?.length > 0 && (
          <button
            className={`${styles.tab} ${styles.mobileOnly} ${activeTab === 'faq' ? styles.active : ''}`}
            onClick={() => setActiveTab('faq')}
            type="button"
            role="tab"
            id={faqTabId}
            aria-controls={faqPanelId}
            aria-selected={activeTab === 'faq'}
          >
            {intl.formatMessage({ id: 'DescriptionRentalTermsTabs.faq' })}
          </button>
        )}
      </nav>

      <div className={styles.tabContent}>
        <section
          id={descriptionPanelId}
          role="tabpanel"
          aria-labelledby={descriptionTabId}
          aria-hidden={activeTab !== 'description'}
          hidden={activeTab !== 'description'}
          className={classNames(styles.tabPanel, {
            [styles.active]: activeTab === 'description',
          })}
        >
          {(conditionBadge || cancelationBadge) && (
            <div className={styles.badges}>
              {conditionBadge && <span className={styles.badge}>{conditionBadge}</span>}
              {cancelationBadge && <span className={styles.badge}>{cancelationBadge}</span>}
            </div>
          )}
          <div className={styles.descriptionContainer}>
            {isHTMLString(truncatedDescription) ? (
              <article
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: truncatedDescription }}
              />
            ) : (
              <pre className={classNames(styles.preDescription, styles.description)}>{truncatedDescription}</pre>
            )}
            {shouldTruncate && (
              <button
                className={styles.readMoreButton}
                onClick={() => setShowFullDescription(!showFullDescription)}
                type="button"
              >
                {showFullDescription ? 'Read Less' : 'Read More'}
                <span
                  className={styles.enhanceCollapseIcon}
                  style={{
                    transform: showFullDescription ? 'rotate(-180deg)' : 'rotate(0deg)',
                  }}
                  aria-hidden="true"

                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"
                      fill="#535862"
                    />
                  </svg>
                </span>
              </button>
            )}
          </div>
        </section>

        <section
          id={rentalTermsPanelId}
          role="tabpanel"
          aria-labelledby={rentalTermsTabId}
          aria-hidden={activeTab !== 'rentalTerms'}
          hidden={activeTab !== 'rentalTerms'}
          className={classNames(styles.tabPanel, {
            [styles.active]: activeTab === 'rentalTerms',
          })}
        >
          <div className={styles.terms}>
            <dl>
              <div className={styles.termRow}>
                <dt className={styles.termLabel}>
                  {intl.formatMessage({
                    id: 'DescriptionRentalTermsTabs.securityDeposit',
                  })}
                </dt>
                <dd className={styles.termValue}>
                  {getSecurityDepositText || '—'}
                </dd>
              </div>

              <div className={styles.termRow}>
                <dt className={styles.termLabel}>
                  {intl.formatMessage({
                    id: 'DescriptionRentalTermsTabs.cancellationPolicy',
                  })}
                </dt>
                <dd className={styles.termValue}>
                  {getCancellationPolicyText || '—'}
                </dd>
              </div>

              <div className={styles.termRow}>
                <dt className={styles.termLabel}>
                  {intl.formatMessage({
                    id: 'DescriptionRentalTermsTabs.rentalAccessibility',
                  })}
                </dt>
                <dd className={styles.termValue}>
                  {rentalTerms?.rentalAccessibility
                    ?.map(item => intl.formatMessage({ id: `ListingPage.tag.${item}` }))
                    .join(', ') || '—'}
                </dd>
              </div>

              {(Array.isArray(rentalTerms?.trustVerification) && rentalTerms.trustVerification.length > 0) ||
              (!Array.isArray(rentalTerms?.trustVerification) && rentalTerms?.trustVerification) ? (
                <div className={styles.termRow}>
                  <dt className={styles.termLabel}>
                    {intl.formatMessage({
                      id: 'DescriptionRentalTermsTabs.trustAndVerification',
                    })}
                  </dt>
                  <dd className={styles.termValue}>
                    {Array.isArray(rentalTerms?.trustVerification)
                      ? rentalTerms.trustVerification
                        .map(item => intl.formatMessage({ id: `ListingPage.tag.${item}` }))
                        .join(', ')
                      : intl.formatMessage({
                        id: `ListingPage.tag.${rentalTerms?.trustVerification}`,
                      })}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </section>

        {faqs?.length > 0 ? (
          <section
            id={faqPanelId}
            role="tabpanel"
            aria-labelledby={faqTabId}
            aria-hidden={activeTab !== 'faq'}
            hidden={activeTab !== 'faq'}
            className={classNames(styles.tabPanel, {
              [styles.active]: activeTab === 'faq',
            })}
          >
            <div className={styles.faqContent}>
              <FAQ faq={faqs} />
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
}
